/**
 * Three things a claim's records can say that no pass produced: where it
 * stands, whether the same claim was settled somewhere else in the library, and
 * what date it promised to resolve by.
 *
 * All three are computed from stored records — `core/standing.ts`,
 * `core/claimIndex.ts`, `core/resolution.ts` — and none of them calls a model or
 * offers a press that spends. The rule each one fired on is printed, the way a
 * cue prints its own: a label with no rule behind it is a verdict handed down.
 * Printed one press away, in the block's fold, unless the label is one the
 * reader must see at rest (D335) — the line at rest says the label, what has
 * been drawn and the date, and the fold holds the receipt and the editors.
 *
 * The claim index reads the whole library and is memoised per session id and per
 * document revision, because it parses every stored exploration; a card that
 * rebuilt it on every render would make selecting a node cost a library scan.
 */

import { useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { STANDING_LABEL, STANDING_NOTE, standingOf } from '../core/standing'
import { libraryClaimIndex, twinsOf, type ClaimEntry } from '../core/claimIndex'
import { shelfKeyExcept } from '../core/persist'
import { useStore } from '../core/store'
import { openSession } from '../core/autosave'
import { useDocs } from './useDocs'
import type { ReasonNode } from '../core/types'
import { outcomeLive } from '../core/types'
import { Icon } from './components/Icon'
import { Fold } from './components/Fold'

/**
 * The library index, rebuilt when THE DOCUMENTS IT READS change and at no other
 * time. One entry: there is one library.
 *
 * The key is what `libraryClaimIndex` actually consumes: which exploration is
 * excluded, and the shelf rows of every other one. Not `rev` — this index is
 * built from every exploration EXCEPT the open one, so the open document's
 * revision is provably not an input, and keying on it reparsed and re-shingled
 * the whole library on every keystroke and on every pane toggle, since
 * `setViewInset` bumps `rev` too. Not the shelf revision either, for the same
 * reason from the other side: it moves on the open document's own autosave.
 *
 * `listRecent` reads one small localStorage row per exploration, which is what
 * makes this affordable to ask on every render — the work it is avoiding is
 * unpacking and shingling every stored document. The `savedAt` of each row is
 * exactly "has this document changed since I read it", which is the question.
 *
 * A twin saved or refuted in another tab still reaches this: that arrives as
 * `refreshFromIndexEvent` rewriting the index rows, `useDocs` turns it into a
 * render, and the row's `savedAt` moves. The subscription is what wakes the
 * component; this is what decides whether there is new work to do.
 */
let indexCache: { key: string; ix: ReturnType<typeof libraryClaimIndex> } | null = null
function claimIndexFor(sessionId: string) {
  // `shelfKeyExcept` and not a second copy of it: this key was written here
  // first and `DueList` copied the argument, so when the cross-tab race showed
  // that an index `savedAt` is the wrong thing to key on, there were two places
  // to correct. One function now, and the mirror's version is what it reads.
  const full = `${sessionId}\u0000${shelfKeyExcept(sessionId)}`
  if (indexCache?.key !== full) indexCache = { key: full, ix: libraryClaimIndex(sessionId) }
  return indexCache.ix
}

/**
 * A LOCAL calendar day, not a UTC one.
 *
 * The stored value is local midday on the day the person picked, so a UTC
 * rendering is off by one wherever the offset is large enough to cross
 * midnight: at UTC+13 picking 3 September stores 2 September UTC, and the date
 * input — which reads and writes local days — would redisplay the day before
 * the one just chosen. `toISOString` is right for an artefact somebody else
 * reads and wrong for the field the person typed in.
 */
const day = (t: number): string => {
  const d = new Date(t)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

export function StandingBlock({ node }: { node: ReasonNode }) {
  const session = useStore((s) => s.session)
  const rev = useStore((s) => s.rev)
  // The shelf's own revision, subscribed rather than read: this fold is built
  // from every OTHER stored exploration, so a twin saved next door has to reach
  // it, and neither `session` nor `rev` moves when it does.
  const shelf = useDocs()
  /**
   * And a clock, on the same rule the due list keeps: `overdue` is a fact about
   * NOW and nothing else this card reads represents time, so a card left mounted
   * across its own deadline kept saying "In the due list until…" while the list
   * beside it had already moved on. Two surfaces telling a person different
   * things about one date is worse than either being late.
   *
   * Woken once, at this card's own deadline, and capped at a day — `setTimeout`
   * overflows past about 24.8 of them and a silent overflow fires immediately,
   * forever, which on a promise made a year out is a busy loop.
   */
  const [now, setNow] = useState(() => Date.now())
  const due = node.resolvesBy
  useEffect(() => {
    if (due === undefined || due <= now) return
    const id = setTimeout(() => setNow(Date.now()), Math.min(due - Date.now() + 1000, 86_400_000))
    return () => clearTimeout(id)
  }, [due, now])
  const [twinsOpen, setTwinsOpen] = useState(false)

  const standing = useMemo(() => standingOf(session, node.id), [session, node.id, rev])
  // The same claim elsewhere in the library. Read lazily — only when the reader
  // opens the fold — because the index parses every stored document, and a card
  // that paid for that on mount would make selecting a node slow for a fact
  // most nodes do not have.
  const twins = useMemo<ClaimEntry[] | null>(
    () =>
      twinsOpen
        ? twinsOf(claimIndexFor(session.id), node.claim, {
            exclude: { sessionId: session.id, nodeId: node.id },
          })
        : null,
    [twinsOpen, session.id, node.claim, node.id, rev, shelf],
  )

  if (node.parentId === null) return null
  const dependsOn = (node.dependsOn ?? []).filter((id) => !!session.nodes[id])
  const attacks = (node.attacks ?? []).filter((id) => !!session.nodes[id])
  // A label other than "standing" is a state the reader must see, so its note
  // and the claims it fired on stay at rest; "standing" is the common case and
  // the line alone says it (D335).
  const loud = !!standing && standing.label !== 'standing'
  // The date, in the line, while the promise control would show it: the same
  // `outcomeLive` rule the control below is put away by.
  const promised = node.outcome && outcomeLive(node) ? undefined : due
  const open = (id: string) => {
    useStore.getState().revealNode(id)
    useStore.getState().select(id)
  }
  const named = (ids: string[]) =>
    ids.map((id, i) => (
      <span key={id}>
        {i > 0 ? ', ' : ''}
        <button className="linkish" onClick={() => open(id)}>
          {session.nodes[id]?.label ?? id}
        </button>
      </span>
    ))
  // The summary's clauses, each only when it is set: an edge somebody drew, or a
  // date somebody promised. Nothing drawn and nothing promised says nothing.
  const clauses: Array<{ key: string; body: ReactNode }> = []
  if (dependsOn.length) clauses.push({ key: 'rests', body: <>rests on {named(dependsOn)}</> })
  if (attacks.length) clauses.push({ key: 'against', body: <>argues against {named(attacks)}</> })
  if (promised !== undefined)
    clauses.push({
      key: 'due',
      body: promised < now ? `overdue since ${day(promised)}` : `due ${day(promised)}`,
    })

  return (
    <div className="group" style={{ gap: 'var(--s2)' }}>
      {/* ESSENTIALS FIRST (D335). At rest this is one line — where it stands,
          and only what has actually been drawn or promised — because it sits
          between the check and the six verbs on every card, and seven lines of
          rarely used machinery there pushed the acts the card is read for below
          the fold. The rule, the edge editor, the date and the library search
          are one press away in the fold under it; nothing was removed. */}
      <div className="tierline">
        <span className="eyebrow">Where it stands</span>
        <span className="gnote" style={{ flex: 1, minWidth: 0 }}>
          {clauses.map((c, i) => (
            <span key={c.key}>
              {i > 0 ? ' · ' : ''}
              {c.body}
            </span>
          ))}
        </span>
        <span className="vp-spent">{standing ? STANDING_LABEL[standing.label] : '—'}</span>
      </div>
      {standing && loud && (
        <>
          <p className="gnote">{STANDING_NOTE[standing.label]}</p>
          {standing.via.length > 0 && <p className="gnote gnote--quiet">{named(standing.via)}</p>}
        </>
      )}

      <Fold id="standing" label="Rule, links, due date and library">
        {standing && (
          <>
            {/* The rule, verbatim — the cue contract. Every id it read is in it.
                One press away rather than at rest: it is the label's receipt,
                and a receipt printed on every card was most of what the reader
                met before any verb. */}
            <p className="gnote gnote--quiet">{standing.rule}</p>
            {!loud && standing.via.length > 0 && (
              <p className="gnote gnote--quiet">{named(standing.via)}</p>
            )}
          </>
        )}

        <Edges node={node} dependsOn={dependsOn} attacks={attacks} />

        {/* THE PROMISE. A date the person expects to know by, and nothing else:
            no reminder, no notification, no model. What it buys is the queue —
            `core/resolution.ts` lists every claim across the library that is due
            and has no result yet — which is how outcomes come to accrue instead
            of being recorded only when somebody happens to remember. Put away by
            recording the result it was waiting for — `resolvesBy` itself is never
            cleared, so a live outcome is what retires the promise, here and in
            the queue.

            Which is why this asks `outcomeLive` rather than for a result at all:
            `reviseNode` keeps the outcome and stamps `editedAt`, and a claim
            somebody has since rewritten is an unmeasured proposition carrying a
            date. Hidden on a bare presence check, that date could not be seen or
            moved without first withdrawing a result that genuinely happened. */}
        {node.outcome && outcomeLive(node) ? null : (
          <div className="group" style={{ gap: 4 }}>
            <label className="gnote" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span>Expect an answer by</span>
              <input
                className="input"
                type="date"
                style={{ width: 'auto' }}
                value={node.resolvesBy !== undefined ? day(node.resolvesBy) : ''}
                onChange={(e) => {
                  const [y, m, d] = e.target.value.split('-').map(Number)
                  // Local midday, like the outcome form parses its own date: a
                  // promise made "for the 12th" is the 12th where the person is.
                  const at = y && m && d ? new Date(y, m - 1, d, 12, 0, 0).getTime() : undefined
                  useStore.getState().patchNode(node.id, { resolvesBy: at })
                }}
              />
            </label>
            {node.resolvesBy !== undefined && (
              <p className="gnote gnote--quiet">
                {node.resolvesBy < now
                  ? `Overdue since ${day(node.resolvesBy)}. It sits in the due list until somebody records what happened; nothing here chases you.`
                  : `In the due list until ${day(node.resolvesBy)}, or until a result is recorded.`}
              </p>
            )}
          </div>
        )}

        {!twinsOpen ? (
          <button
            className="linkish"
            onClick={() => setTwinsOpen(true)}
            title="Reads every stored exploration for the same claim. No model runs."
          >
            Look for this claim elsewhere in your library
          </button>
        ) : twins && twins.length > 0 ? (
          <>
            <p className="gnote">
              The same claim is in {new Set(twins.map((t) => t.sessionId)).size} other exploration
              {new Set(twins.map((t) => t.sessionId)).size === 1 ? '' : 's'}.{' '}
              {twins.some((t) => t.refuted)
                ? 'One of them has it refuted — a refutation over there bears on this claim here.'
                : 'None of them has it refuted.'}
            </p>
            {twins.map((t) => (
              <button
                key={`${t.sessionId}:${t.nodeId}`}
                className="linkish"
                style={{ textAlign: 'left' }}
                onClick={() => openSession(t.sessionId, t.nodeId)}
                title={t.claim}
              >
                <Icon name="search" size={11} /> {t.label} — {t.seed}
                {t.refuted ? ' · refuted there' : ''}
              </button>
            ))}
          </>
        ) : (
          <p className="gnote gnote--quiet">
            No other exploration in this library states this claim.
          </p>
        )}
      </Fold>
    </div>
  )
}

/**
 * The two edges a person can draw, and the only place either is written.
 *
 * The tree already carries one parent edge and `bridgeFrom` the extra parents
 * of a synthesis. These are the two relations a reader kept holding in their
 * head instead: what this claim RESTS ON that is not its parent, and what it
 * ARGUES AGAINST. Drawing one is a local edit — no model, no spend — and it
 * changes what `standingMap` says about both ends, which is the whole reason to
 * draw it: a refutation now reaches everything that rests on the refuted claim,
 * across branches, and an attack that is itself refuted stops counting.
 *
 * The picker lists every other claim in the graph by label, in the order the
 * graph holds them. Ids only, capped by the codec at eight per relation, and a
 * node can never point at itself or at its own parent — the select does not
 * offer either, because the tree already carries the second.
 */
function Edges({
  node,
  dependsOn,
  attacks,
}: {
  node: ReasonNode
  dependsOn: string[]
  attacks: string[]
}) {
  const session = useStore((s) => s.session)
  const [drawing, setDrawing] = useState<'dependsOn' | 'attacks' | null>(null)
  const others = session.order.filter(
    (id) =>
      id !== node.id &&
      id !== node.parentId &&
      !!session.nodes[id] &&
      session.nodes[id]!.parentId !== null,
  )

  const draw = (rel: 'dependsOn' | 'attacks', id: string) => {
    const held = (rel === 'dependsOn' ? dependsOn : attacks).filter((x) => x !== id)
    useStore.getState().patchNode(node.id, { [rel]: [...held, id].slice(0, 8) })
    setDrawing(null)
  }
  const cut = (rel: 'dependsOn' | 'attacks', id: string) => {
    const left = (rel === 'dependsOn' ? dependsOn : attacks).filter((x) => x !== id)
    useStore.getState().patchNode(node.id, { [rel]: left.length ? left : undefined })
  }

  return (
    <div className="group" style={{ gap: 4 }}>
      {(['dependsOn', 'attacks'] as const).map((rel) => {
        const held = rel === 'dependsOn' ? dependsOn : attacks
        return (
          <p key={rel} className="gnote gnote--quiet">
            {rel === 'dependsOn' ? 'Rests on' : 'Argues against'}:{' '}
            {held.length === 0 && <span>nothing drawn. </span>}
            {held.map((id) => (
              <span key={id}>
                <button
                  className="linkish"
                  onClick={() => {
                    useStore.getState().revealNode(id)
                    useStore.getState().select(id)
                  }}
                >
                  {session.nodes[id]?.label ?? id}
                </button>
                <button
                  className="linkish"
                  title="Remove this edge. Nothing else changes."
                  onClick={() => cut(rel, id)}
                >
                  ×
                </button>{' '}
              </span>
            ))}
            {drawing === rel ? (
              <select
                className="input"
                style={{ width: 'auto' }}
                autoFocus
                defaultValue=""
                onChange={(e) => e.target.value && draw(rel, e.target.value)}
                onBlur={() => setDrawing(null)}
              >
                <option value="" disabled>
                  Which claim?
                </option>
                {others.map((id) => (
                  <option key={id} value={id}>
                    {session.nodes[id]!.label}
                  </option>
                ))}
              </select>
            ) : (
              others.length > 0 && (
                <button className="linkish" onClick={() => setDrawing(rel)}>
                  draw
                </button>
              )
            )}
          </p>
        )
      })}
    </div>
  )
}
